
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Auth() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [step, setStep] = useState(1); // 1: login/register, 2: OTP, 3: profile
  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    password: '',
    fullName: '',
    otp: ''
  });

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (step === 1) {
      setStep(2); // Go to OTP
    } else if (step === 2) {
      if (isLogin) {
        navigate('/main');
      } else {
        setStep(3); // Go to profile setup
      }
    } else if (step === 3) {
      navigate('/main');
    }
  };

  const handleSkip = () => {
    navigate('/main');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex flex-col">
      {/* Header */}
      <div className="pt-16 pb-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
          <i className="ri-user-line text-2xl text-white"></i>
        </div>
        
        <h1 className="text-2xl font-bold text-gray-800 font-arabic">
          {step === 1 ? (isLogin ? 'تسجيل الدخول' : 'إنشاء حساب جديد') : 
           step === 2 ? 'رمز التحقق' : 'إكمال البيانات'}
        </h1>
        
        <p className="text-gray-600 mt-2 font-arabic">
          {step === 1 ? 'مرحباً بك في تطبيق سامي عبدالسلام' :
           step === 2 ? 'أدخل الرمز المرسل إلى هاتفك' : 'أكمل بياناتك الشخصية'}
        </p>
      </div>

      {/* Form Container */}
      <div className="flex-1 bg-white rounded-t-3xl p-6 shadow-xl">
        {step === 1 && (
          <div className="space-y-6">
            {/* Phone Input */}
            <div>
              <label className="block text-gray-700 font-medium mb-2 font-arabic">رقم الجوال</label>
              <div className="relative">
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="05xxxxxxxx"
                  className="w-full p-4 pr-12 border border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-right"
                  dir="ltr"
                />
                <i className="ri-phone-line absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>

            {/* Email Input (for registration) */}
            {!isLogin && (
              <div>
                <label className="block text-gray-700 font-medium mb-2 font-arabic">البريد الإلكتروني (اختياري)</label>
                <div className="relative">
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="example@email.com"
                    className="w-full p-4 pr-12 border border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  />
                  <i className="ri-mail-line absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                </div>
              </div>
            )}

            {/* Password Input */}
            <div>
              <label className="block text-gray-700 font-medium mb-2 font-arabic">كلمة المرور</label>
              <div className="relative">
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="••••••••"
                  className="w-full p-4 pr-12 border border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                />
                <i className="ri-lock-line absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-xl font-medium hover:shadow-lg transition-all duration-300 font-arabic"
            >
              {isLogin ? 'تسجيل الدخول' : 'إنشاء حساب'}
            </button>

            {/* Toggle Login/Register */}
            <div className="text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="text-blue-500 font-medium font-arabic"
              >
                {isLogin ? 'ليس لديك حساب؟ إنشاء حساب جديد' : 'لديك حساب؟ تسجيل الدخول'}
              </button>
            </div>

            {/* Skip Button */}
            <button
              onClick={handleSkip}
              className="w-full border border-gray-300 text-gray-600 p-4 rounded-xl font-medium hover:bg-gray-50 transition-all duration-300 font-arabic"
            >
              تخطي التسجيل
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            {/* OTP Input */}
            <div>
              <label className="block text-gray-700 font-medium mb-2 font-arabic">رمز التحقق</label>
              <input
                type="text"
                value={formData.otp}
                onChange={(e) => handleInputChange('otp', e.target.value)}
                placeholder="0000"
                className="w-full p-4 text-center text-2xl border border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none tracking-widest"
                maxLength={4}
              />
            </div>

            <button
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-xl font-medium hover:shadow-lg transition-all duration-300 font-arabic"
            >
              تأكيد
            </button>

            <div className="text-center">
              <button className="text-blue-500 font-medium font-arabic">
                إعادة إرسال الرمز
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-gray-700 font-medium mb-2 font-arabic">الاسم الكامل</label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                placeholder="أدخل اسمك الكامل"
                className="w-full p-4 border border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none text-right font-arabic"
              />
            </div>

            <button
              onClick={handleSubmit}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-xl font-medium hover:shadow-lg transition-all duration-300 font-arabic"
            >
              إكمال التسجيل
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        .font-arabic {
          font-family: 'Tajawal', 'Noto Sans Arabic', sans-serif;
        }
      `}</style>
    </div>
  );
}
